import { createContext, useContext, type ReactNode } from 'react';
import type { PortDeFil } from '../conversations/port.js';

/**
 * LE PORT DES CONVERSATIONS POUR LE CENTRE DE COMMUNICATION (chantier
 * `centre-communication`, vague finale du 05/10/2026). Le shell n'a pas le
 * SDK : l'hote monte ce port avec `sdk.conversations`, `sdk.messages` et
 * `sdk.conversations.lecture` (contrat `docs/socle/conversations.md`), et le
 * centre ne connait que ces vues. Les canaux, les messages directs et le
 * chatter de l'objet courant passent par ici ; les sujets de Lia ont le leur
 * (`LiaConversationsPort`).
 */
export type AncreVue = { family: string; recordId: string } | { appId: string; doorId?: string };

export interface ConversationVue {
  id: string;
  type: 'canal' | 'direct' | 'chatter';
  /** Le nom d'un canal ; vide pour un direct ou un chatter (l'ecran nomme les participants ou l'objet). */
  name: string;
  prive: boolean;
  /** Les `personId` des participants d'un prive (direct, canal prive) ; vide sinon. */
  participants: string[];
  proprietaire: string | null;
  /** L'ancre d'un chatter : la fiche (`acces.ancre`) ou la porte/app (`ancre`). */
  ancre: AncreVue | null;
  modifieLe: number | null;
}

/**
 * Une fiche lue pour la colonne « Fiche suivie » : ses champs renseignes, en
 * lecture seule. `cle` est le chemin de stockage (`firstName`,
 * `customFields.city`) : le centre le nomme avec les libelles de la porte.
 */
export interface FicheLueVue {
  titre: string;
  type?: string;
  champs: { cle?: string; libelle: string; valeur: string }[];
}

export interface PreferenceConversationView { ref: string; sourdine: boolean; titre?: string; notificationsExternes?: { actif: boolean; delaiMinutes: number; canaux: Array<'email' | 'whatsapp'>; activeDepuis: number } }
export interface PastillesPort {
  parConversation: Record<string, number>;
  dernierAt: Record<string, Date | null>;
  total: number; arrivees: number; erreur: string | null;
  preferences: PreferenceConversationView[];
  suivre(ref: string): void;
  regarder(ref: string | null): void;
  marquerAffiche(ref: string): void;
  marquerLiaAffiche(ref: string, date: Date, docId?: string): void;
}

export interface ConversationsCentrePort {
  pastilles?: PastillesPort;
  sources?: ConversationsCentrePort;
  observerSuivies?(onChange: (conversations: ConversationVue[]) => void, onError?: (e: unknown) => void): () => void;
  observerCanaux(onChange: (canaux: ConversationVue[]) => void, onError?: (error: unknown) => void): () => void;
  observerDirects(onChange: (directs: ConversationVue[]) => void, onError?: (error: unknown) => void): () => void;
  lire(conversationId: string): Promise<ConversationVue | null>;
  creerCanal(input: { nom: string; description?: string; prive?: boolean; participants?: string[] }): Promise<ConversationVue>;
  /** Rend le direct EXISTANT entre ces personnes (moi compris) s'il existe. */
  creerDirect(personIds: string[]): Promise<ConversationVue>;
  /** `null` tant qu'aucun message n'a ete ecrit sur cet objet. */
  chatterDe(ancre: AncreVue): Promise<ConversationVue | null>;
  /** Le cree au premier message, le retrouve ensuite. */
  ouvrirChatter(ancre: AncreVue): Promise<ConversationVue>;
  /**
   * La base de cette fiche a-t-elle « Chatter sur les fiches » allume ? (Tour 3
   * du contrat du socle, lu par le serveur.) Absent : le socle ne le sait pas
   * encore dire, le chatter est tenu pour allume.
   */
  chatterActif?(ancre: { family: string; recordId: string }): Promise<boolean>;
  lecture: {
    observer(onChange: (lu: Record<string, Date>) => void, onError?: (error: unknown) => void): () => void;
    marquerLu(conversationId: string, jusquA?: Date): Promise<void>;
    /** Compte serveur des messages posterieurs a `lu` (G15). */
    nonLus(conversationId: string, lu: Date | undefined): Promise<number>;
  };
  /** Le fil et le composeur du socle lisent et ecrivent par la. */
  fil: PortDeFil;
  /**
   * RENOMMER ET ARCHIVER (R4 du soir, contrat du socle) : un canal se renomme
   * (nom unique dans l'org, sinon une erreur qui le dit) ; « Supprimer »
   * archive un canal pour tous. Un direct ne se supprime pas (P18 du
   * 06/10/2026 : le SDK le refuse, `DirectNonSupprimable`). `peutGerer` dit
   * si le geste est permis, pour le griser avec sa raison (G16).
   */
  renommer(conversationId: string, nom: string): Promise<void>;
  archiver(conversationId: string): Promise<void>;
  peutGerer(conversationId: string): Promise<boolean>;
  /**
   * L'EPINGLAGE PERSONNEL (R12), propre a chaque personne et garde par le
   * serveur. Une reference : `lia:<sujetId>` ou `conversation:<id>` (canal,
   * direct, chatter de fiche), dans l'ordre d'epinglage.
   */
  epingles: {
    observer(onChange: (refs: string[]) => void, onError?: (error: unknown) => void): () => void;
    epingler(ref: string): Promise<void>;
    desepingler(ref: string): Promise<void>;
  };
  /** La fiche suivie, lue avec les droits du membre (colonne droite du centre agrandi). Absent : rien a montrer. */
  lireFiche?(family: string, recordId: string): Promise<FicheLueVue | null>;
}

const Context = createContext<ConversationsCentrePort | null>(null);

export function ConversationsCentreProvider({ value, children }: { value: ConversationsCentrePort; children: ReactNode }) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

/** `null` quand l'hote ne monte pas le port : les sources restent des emplacements qui le disent (G16). */
export function useConversationsCentrePort(): ConversationsCentrePort | null {
  return useContext(Context);
}
